import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { CurrencyInr, Buildings, Users, ShoppingBag, Truck, Crown } from "@phosphor-icons/react";

export default function SuperDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="p-6">Loading…</div>;
  const { stats, top_stores, delivery_metrics, recent_orders } = data;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 fade-up">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">Platform overview</h1>
        <p className="text-gray-600 text-sm">Real-time across all stores</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Tile icon={CurrencyInr} label="GMV" value={`₹${Math.round(stats.gmv).toLocaleString("en-IN")}`} color="#15803D" testId="super-tile-gmv" />
        <Tile icon={Buildings} label="Stores" value={stats.total_stores} color="#F8CB46" testId="super-tile-stores" />
        <Tile icon={Users} label="Customers" value={stats.active_customers} color="#3B82F6" testId="super-tile-customers" />
        <Tile icon={ShoppingBag} label="Today's orders" value={stats.orders_today} color="#F59E0B" testId="super-tile-orders" />
        <Tile icon={Crown} label="Sub revenue" value={`₹${stats.subscription_revenue}`} color="#A855F7" testId="super-tile-subrev" />
        <Tile icon={CurrencyInr} label="Commission" value={`₹${stats.commission_revenue}`} color="#15803D" testId="super-tile-commission" />
        <Tile icon={Truck} label="Avg ETA" value={`${delivery_metrics.avg_eta} min`} color="#EF4444" testId="super-tile-eta" />
        <Tile icon={ShoppingBag} label="Delivered" value={stats.delivered} color="#15803D" testId="super-tile-delivered" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="qd-card p-5">
          <div className="text-xs uppercase tracking-widest font-bold text-gray-500">Top performing</div>
          <h3 className="font-heading text-xl font-bold mb-4">Stores by orders</h3>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={top_stores} layout="vertical">
                <CartesianGrid stroke="#F1F1F4" />
                <XAxis type="number" tickLine={false} style={{ fontSize: 12 }} />
                <YAxis dataKey="name" type="category" tickLine={false} axisLine={false} style={{ fontSize: 11 }} width={100} />
                <Tooltip cursor={{ fill: "#F8CB4622" }} />
                <Bar dataKey="orders" fill="#15803D" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="qd-card p-5">
          <div className="text-xs uppercase tracking-widest font-bold text-gray-500">Delivery split</div>
          <h3 className="font-heading text-xl font-bold mb-4">Self vs 3rd party</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-semibold">Self delivery</span>
                <span className="text-gray-500">{delivery_metrics.self_delivery} orders</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#15803D]" style={{ width: `${(delivery_metrics.self_delivery / Math.max(stats.total_orders, 1)) * 100}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-semibold">3rd party (Borzo/Porter)</span>
                <span className="text-gray-500">{delivery_metrics.third_party} orders</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#F8CB46]" style={{ width: `${(delivery_metrics.third_party / Math.max(stats.total_orders, 1)) * 100}%` }} />
              </div>
            </div>
            <div className="pt-4 border-t border-gray-100 text-sm text-gray-600">
              Average ETA across all deliveries: <b className="text-gray-900">{delivery_metrics.avg_eta} minutes</b>
            </div>
          </div>
        </div>
      </div>

      <div className="qd-card p-5">
        <h3 className="font-heading text-xl font-bold mb-3">Recent orders</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 font-semibold">Order</th>
                <th className="py-2 font-semibold">Customer</th>
                <th className="py-2 font-semibold">Total</th>
                <th className="py-2 font-semibold">Status</th>
                <th className="py-2 font-semibold">Mode</th>
                <th className="py-2 font-semibold">Pincode</th>
              </tr>
            </thead>
            <tbody>
              {recent_orders.map((o) => (
                <tr key={o.id} className="border-b border-gray-50">
                  <td className="py-3 font-mono text-xs">{o.order_number}</td>
                  <td className="py-3">{o.customer_name}</td>
                  <td className="py-3 font-bold">₹{o.total}</td>
                  <td className="py-3 capitalize">{o.status.replace(/_/g, " ")}</td>
                  <td className="py-3 capitalize">{o.delivery_mode}</td>
                  <td className="py-3 font-mono text-xs">{o.pincode}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, color, testId }) {
  return (
    <div className="qd-card p-4" data-testid={testId}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: `${color}22` }}>
        <Icon weight="duotone" size={20} style={{ color }} />
      </div>
      <div className="text-xs text-gray-500 uppercase tracking-wide font-bold">{label}</div>
      <div className="font-heading font-black text-xl">{value}</div>
    </div>
  );
}
